// src/components/dashboard/defaultLayouts.js
// Default chart-card layouts per workshop for DashboardGrid.
// Units: 12-column grid (x, w) + RGL row units (y, h) at rowHeight 40.
// Compact by default: standard charts share 3-per-row slots (w:4); full-width
// (w:12) is reserved for matrices (risk/residual), the recap-adjacent
// comparison zone, budget bars, and the W3 radar. W4 pairs coverage (w:4)
// with the skill heatmap (w:8) on one row. Rows stack tightly
// (y = running offset).
// Card ids follow `<wN>.<slug>` and match the current visual order/sizes.
// Conditional cards keep their slot; missing cards are compacted vertically.

// W1 — Workshop1Dashboard (dreaded-events overview: pillars, severity, status).
export const W1_DEFAULT_LAYOUT = {
  'w1-pillarBars': { x: 0, y: 0, w: 4, h: 2 },
  'w1-severityDonut': { x: 4, y: 0, w: 4, h: 2 },
  'w1-statusDonut': { x: 8, y: 0, w: 4, h: 2 },
  'w1-bvPillarHeatmap': { x: 0, y: 2, w: 4, h: 2 },
  'w1-timeline': { x: 4, y: 2, w: 4, h: 2 },
  'w1-impactBars': { x: 8, y: 2, w: 4, h: 2 }
};

// W2 — Workshop2Dashboard (risk sources: categories, diagnostic, levels).
export const W2_DEFAULT_LAYOUT = {
  'w2-categoryBars': { x: 0, y: 0, w: 4, h: 2 },
  'w2-diagnosticHeatmap': { x: 4, y: 0, w: 4, h: 2 },
  'w2-niveauDonut': { x: 8, y: 0, w: 4, h: 2 },
  'w2-sourceEventMap': { x: 0, y: 2, w: 4, h: 2 }
};

// W3 — Workshop3Dashboard (strategic scenarios: scatter, residual, zones, radar).
export const W3_DEFAULT_LAYOUT = {
  'w3-scatter': { x: 0, y: 0, w: 4, h: 2 },
  'w3-initialResidual': { x: 4, y: 0, w: 4, h: 2 },
  'w3-zoneOrigin': { x: 8, y: 0, w: 4, h: 2 },
  'w3-priorityHeatmap': { x: 0, y: 2, w: 4, h: 2 },
  'w3-radar': { x: 0, y: 4, w: 12, h: 2 }
};

// W4 — Workshop4Dashboard (operational scenarios: risk matrix, coverage, skills).
export const W4_DEFAULT_LAYOUT = {
  'w4-riskMatrix': { x: 0, y: 0, w: 12, h: 3 },
  'w4-coverage': { x: 0, y: 3, w: 4, h: 2 },
  'w4-skillHeatmap': { x: 4, y: 3, w: 8, h: 2 }
};

// W5 — Workshop5Charts (risk treatment: comparison, options, residual, budget).
export const W5_DEFAULT_LAYOUT = {
  'w5-comparison': { x: 0, y: 0, w: 12, h: 2 },
  'w5-options': { x: 0, y: 2, w: 4, h: 2 },
  'w5-residualMatrix': { x: 0, y: 4, w: 12, h: 3 },
  'w5-budget': { x: 0, y: 7, w: 12, h: 2 }
};

// Per-card resize floors (grid units) matched to content. Every min is <=
// its default w/h above, so default sizes never shift. minH rows -> px at
// rowHeight 40 + margin 8: minH 2 -> 88px, minH 3 -> 136px; chart minHeight
// style floors stay >= those values.
export const CARD_MIN = {
  'w1-pillarBars': { minW: 3, minH: 2 },
  'w1-severityDonut': { minW: 2, minH: 2 },
  'w1-statusDonut': { minW: 2, minH: 2 },
  'w1-bvPillarHeatmap': { minW: 4, minH: 2 },
  'w1-timeline': { minW: 4, minH: 2 },
  'w1-impactBars': { minW: 3, minH: 2 },
  'w2-categoryBars': { minW: 3, minH: 2 },
  'w2-diagnosticHeatmap': { minW: 4, minH: 2 },
  'w2-niveauDonut': { minW: 2, minH: 2 },
  'w2-sourceEventMap': { minW: 4, minH: 2 },
  'w3-scatter': { minW: 4, minH: 2 },
  'w3-initialResidual': { minW: 3, minH: 2 },
  'w3-zoneOrigin': { minW: 3, minH: 2 },
  'w3-priorityHeatmap': { minW: 4, minH: 2 },
  'w3-radar': { minW: 6, minH: 2 },
  'w4-riskMatrix': { minW: 8, minH: 3 },
  'w4-coverage': { minW: 2, minH: 2 },
  'w4-skillHeatmap': { minW: 6, minH: 2 },
  'w5-comparison': { minW: 6, minH: 2 },
  'w5-options': { minW: 2, minH: 2 },
  'w5-residualMatrix': { minW: 8, minH: 3 },
  'w5-budget': { minW: 6, minH: 2 }
};

// Original pre-compact heights (first generation of saved layouts).
export const LEGACY_DEFAULT_H = {
  'w1-pillarBars': 5,
  'w1-severityDonut': 5,
  'w1-statusDonut': 5,
  'w1-bvPillarHeatmap': 6,
  'w1-timeline': 5,
  'w1-impactBars': 5,
  'w2-categoryBars': 6,
  'w2-diagnosticHeatmap': 5,
  'w2-niveauDonut': 5,
  'w2-sourceEventMap': 6,
  'w3-scatter': 5,
  'w3-initialResidual': 6,
  'w3-zoneOrigin': 5,
  'w3-priorityHeatmap': 5,
  'w3-radar': 6,
  'w4-riskMatrix': 7,
  'w4-coverage': 5,
  'w4-skillHeatmap': 5,
  'w5-comparison': 6,
  'w5-options': 5,
  'w5-residualMatrix': 7,
  'w5-budget': 6
};

// Compact-generation heights (second generation of saved layouts: every
// card now h:2 was h:4, every card now h:3 was h:5).
export const COMPACT_DEFAULT_H = {
  'w1-pillarBars': 4,
  'w1-severityDonut': 4,
  'w1-statusDonut': 4,
  'w1-bvPillarHeatmap': 4,
  'w1-timeline': 4,
  'w1-impactBars': 4,
  'w2-categoryBars': 4,
  'w2-diagnosticHeatmap': 4,
  'w2-niveauDonut': 4,
  'w2-sourceEventMap': 4,
  'w3-scatter': 4,
  'w3-initialResidual': 4,
  'w3-zoneOrigin': 4,
  'w3-priorityHeatmap': 4,
  'w3-radar': 4,
  'w4-riskMatrix': 5,
  'w4-coverage': 4,
  'w4-skillHeatmap': 4,
  'w5-comparison': 4,
  'w5-options': 4,
  'w5-residualMatrix': 5,
  'w5-budget': 4
};
